import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { isBillingBlockedError } from '@/services/api';

/**
 * Parse a `[0,1]` sample rate from an env var. Falls back to `fallback` for
 * undefined / empty / NaN inputs and clamps the result to [0, 1] so a
 * misconfigured env var (`'abc'`, `'0,1'`, `'2'`) never breaks the SDK.
 */
function parseSampleRate(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw === '') return fallback;
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(1, Math.max(0, parsed));
}

/**
 * Initialize Sentry. Safe no-op when VITE_SENTRY_DSN is not configured.
 *
 * Privacy posture:
 * - sendDefaultPii: false — never auto-attach IPs/headers
 * - Replay masks all text/inputs and blocks media
 * - tracesSampleRate defaults to 0 (errors only) to stay within Sentry free
 *   tier (10k transactions/mo). Set VITE_SENTRY_TRACES_SAMPLE_RATE explicitly
 *   (e.g. 0.001) to enable tracing. Same applies to replay sample rates.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  if (!dsn) {
    return;
  }

  const tracesSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined,
    0,
  );
  const replaysSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string | undefined,
    0,
  );
  const replaysOnErrorSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE as string | undefined,
    1.0,
  );

  Sentry.init({
    dsn,
    // VITE_SENTRY_ENVIRONMENT lets CI/CD override Vite's MODE so staging builds
    // report as environment:staging rather than environment:production (MODE is
    // always 'production' for Vite prod builds regardless of deploy target).
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
      import.meta.env.MODE,
    release: (import.meta.env.VITE_GIT_COMMIT as string | undefined) || 'unknown',
    enabled: true,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
  });
}

/**
 * Wrap an arbitrary error captured by react-query (queries or mutations) and
 * forward it to Sentry along with optional structured context.
 *
 * Filters out *expected* HTTP error statuses (401/402/403/404/422/429) that
 * `handleQueryError` already treats as normal UX flows — forwarding them to
 * Sentry would produce noise and inflate the free-tier quota. Network errors
 * (no response) and 5xx responses are always forwarded.
 *
 * 402 = billing paywall (`BILLING_*`) and 403 = `ORG_SUSPENDED` are EXPECTED
 * billing-block states (Epic #884 §3.1): the Axios interceptor routes them to
 * the PaywallScreen / AccountUnavailableScreen, so they are normal UX — not
 * crashes. Capturing them would flood Sentry while a delinquent tenant polls.
 *
 * 422 = business-rule validations (`BUSINESS_*` DomainExceptions) such as
 * "won requires value" or "lost requires reason". These are surfaced to the
 * user via toasts in the UI and are expected flows — per CODING_STANDARDS,
 * expected 4xx must never be sent to Sentry.
 */
const EXPECTED_QUERY_ERROR_STATUSES = new Set([401, 402, 403, 404, 422, 429]);

interface MediaFetchErrorContext {
  [key: string]: unknown;
  messageId: string;
  attempts: number;
  httpStatus?: number;
  apiCode?: string;
  responseStatus?: string;
  kind: string;
}

const EXPECTED_MEDIA_NOT_FOUND_CODES = new Set([
  'MEDIA_STORAGE_KEY_MISSING',
  'MEDIA_STORAGE_OBJECT_MISSING',
  'MEDIA_FAILED',
  'MEDIA_EXPIRED',
]);
const EXPECTED_MEDIA_CLIENT_ERROR_STATUSES = new Set([401, 403, 429]);

export function captureQueryError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  // Synthetic "request blocked by paywall" error (Epic #884 §3.1): the request
  // never left the browser, so it is never a real failure — never forward it to
  // Sentry. Checked first so it short-circuits before the AxiosError branch.
  if (isBillingBlockedError(error)) {
    return;
  }

  if (error instanceof AxiosError) {
    const status = error.response?.status;
    // Drop known UX-handled client errors. Network errors (`status === undefined`)
    // and any 5xx still flow through to Sentry.
    if (status !== undefined && EXPECTED_QUERY_ERROR_STATUSES.has(status)) {
      return;
    }
  }

  Sentry.captureException(error, {
    contexts: {
      query: context ?? {},
    },
  });
}

/**
 * Capture authenticated media fetch failures with sanitized context only.
 *
 * We intentionally avoid forwarding the raw Axios error because it may contain
 * request config details we don't need for diagnosis. Expected media
 * processing/not-found states are skipped to reduce Sentry noise.
 */
export function captureMediaFetchError(
  context: MediaFetchErrorContext,
): void {
  if (
    context.responseStatus === 'pending' ||
    context.apiCode === 'MEDIA_SIZE_UNAVAILABLE'
  ) {
    return;
  }

  if (
    context.httpStatus === 404 &&
    context.apiCode &&
    EXPECTED_MEDIA_NOT_FOUND_CODES.has(context.apiCode)
  ) {
    return;
  }

  if (
    context.httpStatus !== undefined &&
    EXPECTED_MEDIA_CLIENT_ERROR_STATUSES.has(context.httpStatus)
  ) {
    return;
  }

  Sentry.captureException(new Error('Authenticated media fetch failed'), {
    contexts: {
      mediaFetch: context,
    },
  });
}
